'use client';

import { lazy, type ComponentType } from 'react';

/**
 * Lazily load the regular processor edit pages so the setup payment step can
 * embed them. Named exports are unwrapped into React.lazy default shapes;
 * SetupProcessorEditEmbed provides the Suspense boundary.
 */
function lazyNamed(loader: () => Promise<Record<string, ComponentType>>, exportName: string): ComponentType {
  return lazy(() => loader().then((module) => ({ default: module[exportName]! })));
}

const AdyenEditPage = lazyNamed(() => import('@/app/processors/[id]/adyen/edit/page'), 'AdyenEditPage');
const AirwallexEditPage = lazyNamed(() => import('@/app/processors/[id]/airwallex/edit/page'), 'AirwallexEditPage');
const AuthorizeNetEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/authorizenet/edit/page'), 'AuthorizeNetEditPageContainer');
const BakolaPayEditPage = lazyNamed(() => import('@/app/processors/[id]/bakolapay/edit/page'), 'BakolaPayEditPage');
const BlueSnapEditPage = lazyNamed(() => import('@/app/processors/[id]/bluesnap/edit/page'), 'BlueSnapEditPage');
const BraintreeEditPage = lazyNamed(() => import('@/app/processors/[id]/braintree/edit/page'), 'BraintreeEditPage');
const CCAvenueEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/ccavenue/edit/page'), 'CCAvenueEditPageContainer');
const CheckoutEditPage = lazyNamed(() => import('@/app/processors/[id]/checkout/edit/page'), 'CheckoutEditPage');
const ConvesioEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/convesio/edit/page'), 'ConvesioEditPageContainer');
const EfiPaymentsEditPage = lazyNamed(() => import('@/app/processors/[id]/efipayments/edit/page'), 'EfiPaymentsEditPage');
const FinixEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/finix/edit/page'), 'FinixEditPageContainer');
const FiservEditPage = lazyNamed(() => import('@/app/processors/[id]/fiserv/edit/page'), 'FiservEditPage');
const GlobalPayEditPage = lazyNamed(() => import('@/app/processors/[id]/globalpay/edit/page'), 'GlobalPayEditPage');
const KessPayEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/kesspay/edit/page'), 'KessPayEditPageContainer');
const MastercardEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/mastercard/edit/page'), 'MastercardEditPageContainer');
const MerchantScaleEditPage = lazyNamed(() => import('@/app/processors/[id]/merchantscale/edit/page'), 'MerchantScaleEditPage');
const MollieEditPage = lazyNamed(() => import('@/app/processors/[id]/mollie/edit/page'), 'MollieEditPage');
const NMIEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/nmi/edit/page'), 'NMIEditPageContainer');
const NetValveEditPage = lazyNamed(() => import('@/app/processors/[id]/netvalve/edit/page'), 'NetValveEditPage');
const XoalaEditPage = lazyNamed(() => import('@/app/processors/[id]/xoala/edit/page'), 'XoalaEditPage');
const UnlimitEditPage = lazyNamed(() => import('@/app/processors/[id]/unlimit/edit/page'), 'UnlimitEditPage');
const OnyxEditPage = lazyNamed(() => import('@/app/processors/[id]/onyx/edit/page'), 'OnyxEditPage');
const NgeniusEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/ngenius/edit/page'), 'NgeniusEditPageContainer');
const OvriEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/ovri/edit/page'), 'OvriEditPageContainer');
const PayOneEditPage = lazyNamed(() => import('@/app/processors/[id]/payone/edit/page'), 'PayOneEditPage');
const PayUEditPage = lazyNamed(() => import('@/app/processors/[id]/payu/edit/page'), 'PayUEditPage');
const PaylandEditPage = lazyNamed(() => import('@/app/processors/[id]/payland/edit/page'), 'PaylandEditPage');
const PaysafeEditPage = lazyNamed(() => import('@/app/processors/[id]/paysafe/edit/page'), 'PaysafeEditPage');
const PaystackEditPage = lazyNamed(() => import('@/app/processors/[id]/paystack/edit/page'), 'PaystackEditPage');
const RazorpayEditPage = lazyNamed(() => import('@/app/processors/[id]/razorpay/edit/page'), 'RazorpayEditPage');
const SandboxEditPage = lazyNamed(() => import('@/app/processors/[id]/sandbox/edit/page'), 'SandboxEditPage');
const StripeEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/stripe/edit/page'), 'StripeEditPageContainer');
const TagadaPayEditPage = lazyNamed(() => import('@/app/processors/[id]/tagadapay/edit/page'), 'TagadaPayEditPage');
const TagadaPayRouterEditPage = lazyNamed(() => import('@/app/processors/[id]/tagadapay-router/edit/page'), 'TagadaPayRouterEditPage');
const TapEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/tap/edit/page'), 'TapEditPageContainer');
const TrustFlowEditPageContainer = lazyNamed(() => import('@/app/processors/[id]/trustflow/edit/page'), 'TrustFlowEditPageContainer');
const WorldpayEditPage = lazyNamed(() => import('@/app/processors/[id]/worldpay/edit/page'), 'WorldpayEditPage');

export const SETUP_PROCESSOR_EDIT_COMPONENTS: Record<string, ComponentType> = {
  stripe: StripeEditPageContainer,
  airwallex: AirwallexEditPage,
  checkout: CheckoutEditPage,
  nmi: NMIEditPageContainer,
  onyx: OnyxEditPage,
  finix: FinixEditPageContainer,
  tagadapay: TagadaPayEditPage,
  'tagadapay-router': TagadaPayRouterEditPage,
  efipayments: EfiPaymentsEditPage,
  merchantscale: MerchantScaleEditPage,
  bakolapay: BakolaPayEditPage,
  sandbox: SandboxEditPage,
  mastercard: MastercardEditPageContainer,
  trustflow: TrustFlowEditPageContainer,
  kesspay: KessPayEditPageContainer,
  ovri: OvriEditPageContainer,
  ccavenue: CCAvenueEditPageContainer,
  ngenius: NgeniusEditPageContainer,
  tap: TapEditPageContainer,
  authorizenet: AuthorizeNetEditPageContainer,
  convesio: ConvesioEditPageContainer,
  adyen: AdyenEditPage,
  payland: PaylandEditPage,
  paysafe: PaysafeEditPage,
  bluesnap: BlueSnapEditPage,
  worldpay: WorldpayEditPage,
  braintree: BraintreeEditPage,
  mollie: MollieEditPage,
  fiserv: FiservEditPage,
  paystack: PaystackEditPage,
  payu: PayUEditPage,
  razorpay: RazorpayEditPage,
  payone: PayOneEditPage,
  globalpay: GlobalPayEditPage,
  netvalve: NetValveEditPage,
  xoala: XoalaEditPage,
  unlimit: UnlimitEditPage,
};

export function getSetupProcessorEditComponent(type: string): ComponentType | null {
  return SETUP_PROCESSOR_EDIT_COMPONENTS[type] ?? null;
}
